// TODO: This state should be separated into two states, one form manage views (on admin), and another for view portal (core).
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import {
  iif,
  insertItem,
  patch,
  removeItem,
  updateItem,
} from '@ngxs/store/operators';
import { DataViewDataModel, RequestControllerService } from '@osos/shared/api';
import { removeNullValues } from '@osos/shared/legacy-common';
import {
  anonymousViews,
  authenticatedViews,
  BreadcrumbToken,
  Condition,
  DataViewFilters,
  DataViewsForAdminPresenterModel,
  DataViewsPermissionsForEndUserPresenterModel,
  DataViewsPresenterModel,
  LoadingIndicator,
  ManagedHttpContext,
  Page,
  PermissionScopeCounts,
  RefProperty,
  RestrictedViews,
  ServiceRequestSource,
  Where,
} from '@osos/shared/types';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { EndUserDataViewsFactoryService } from '../../factories';
import { DataViewsFactoryService } from '../../factories/data-views-factory/data-views-factory.service';
import { DataViewsPermissionsForEndUserFactoryService } from '../../factories/data-views-factory/data-views-permissions-for-end-user-factory.service';
import {
  ApiUtilsService,
  BreadcrumbService,
  ViewDataService,
} from '../../services';
import { AppStateActions } from '../app/app.actions';
import { EntityActions } from '../entity-data-state/entity.action';
import { LoadingActions } from '../loading/loading.actions';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { ViewActions } from './view.action';

const serviceRequestSourceMap = new Map([
  [ServiceRequestSource.Inbox, 'Inbox'],
  [ServiceRequestSource.My, 'MyRequest'],
  [ServiceRequestSource.RequestMonitoring, 'Observer'],
]);

export interface ViewStateModel {
  page: Page<DataViewsPresenterModel>;
  presentedView: { [key: string]: DataViewsPresenterModel };
  selectedView?: string;
  presentedViews: DataViewsPresenterModel[];
  contextView: Record<string, { value: string; endUser?: boolean }>;

  publishedViews: Partial<DataViewsPresenterModel>[];
  defaultView?: DataViewsPresenterModel;

  presentedViewForEndUser: { [key: string]: DataViewsPresenterModel };
  presentedViewPermissionsForEndUser: {
    [key: string]: DataViewsPermissionsForEndUserPresenterModel;
  };

  presentedViewByRelation: { [key: string]: DataViewsPresenterModel };

  /** An index for implicit view key maped to data view Id */
  implicitView: { [key: string]: string };

  /**
   * temporary state to store views and select them once
   */
  transitoryViews: DataViewsPresenterModel[];
  viewsForAdminCount: number;
  ViewsForAdmin: DataViewsForAdminPresenterModel[];
  loading: LoadingIndicator;
  counts?: PermissionScopeCounts;
  lastFetchedView: RefProperty;
  lastCreatedView?: string;
}

@State<ViewStateModel>({
  name: 'views',
  defaults: {
    page: {
      items: [],
      totalCount: 0,
    },
    presentedView: {},
    contextView: {},
    presentedViews: [],
    publishedViews: [],
    transitoryViews: [],
    viewsForAdminCount: 0,
    ViewsForAdmin: [],
    presentedViewForEndUser: {},
    presentedViewPermissionsForEndUser: {},
    presentedViewByRelation: {},
    implicitView: {},
    loading: new LoadingIndicator(),
    counts: {
      anonymous: 0,
      authenticated: 0,
      restricted: 0,
    },
    lastFetchedView: {} as RefProperty,
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
// @TODO because view returned in public portly is different from view returned in admin view
// we need separate them for 2 states
// PublicViewState has no cache - no sync required with admin view state
// AdminViewState with cache
// @ToDiscuss caching in web storage for end user views
export class ViewState {
  constructor(
    private dataViewsService: ViewDataService,
    private requestControllerService: RequestControllerService,
    private apiHelper: ApiUtilsService,
    private dataViewsFactoryService: DataViewsFactoryService,
    private endUserDataViewsFactoryService: EndUserDataViewsFactoryService,
    private dataViewsPermissionsForEndUserFactoryService: DataViewsPermissionsForEndUserFactoryService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------
  @Selector([ViewState])
  static loadingIndicator(state: ViewStateModel): LoadingIndicator {
    return state.loading;
  }

  @Selector([ViewState])
  static publishedViews(
    state: ViewStateModel,
  ): Partial<DataViewsPresenterModel>[] {
    return state.publishedViews;
  }

  @Selector([ViewState])
  static presentedViews(state: ViewStateModel): DataViewsPresenterModel[] {
    return state.presentedViews;
  }

  @Selector([ViewState])
  static scopesCounts(state: ViewStateModel): PermissionScopeCounts {
    return state.counts;
  }

  @Selector([ViewState])
  static lastFetchView(state: ViewStateModel): RefProperty {
    return state.lastFetchedView;
  }
  @Selector([ViewState])
  static lastCreatedView(state: ViewStateModel): string {
    return state.lastCreatedView;
  }
  @Selector([ViewState])
  private static presentedViewMap(state: ViewStateModel) {
    return state.presentedView;
  }

  @Selector([ViewState])
  private static presentedViewByRelationMap(state: ViewStateModel) {
    return state.presentedViewByRelation;
  }

  @Selector([ViewState])
  private static presentedViewForEndUserMap(state: ViewStateModel) {
    return state.presentedViewForEndUser;
  }

  @Selector([ViewState])
  private static presentedViewPermissionsForEndUserMap(state: ViewStateModel) {
    return state.presentedViewPermissionsForEndUser;
  }

  @Selector([ViewState])
  private static contextViewMap(state: ViewStateModel) {
    return state.contextView;
  }

  static presentedView(entityId: string, viewId: string) {
    return createSelector(
      [ViewState.presentedViewMap],
      (presentedViewMap) => presentedViewMap[_getViewKey(entityId, viewId)],
    );
  }

  static presentedViewByRelation(
    entityId: string,
    viewId: string,
    relationId: string,
    relatedEntryId?: string,
  ) {
    return createSelector(
      [ViewState.presentedViewByRelationMap],
      (presentedViewByRelationMap) =>
        presentedViewByRelationMap[
          _getRelationViewKey(entityId, viewId, relationId, relatedEntryId)
        ],
    );
  }

  static presentedViewForEndUser(viewId: string) {
    return createSelector(
      [ViewState.presentedViewForEndUserMap],
      (presentedViewForEndUserMap) => presentedViewForEndUserMap[viewId],
    );
  }

  static presentedViewPermissionsForEndUser(viewId: string) {
    return createSelector(
      [ViewState.presentedViewPermissionsForEndUserMap],
      (presentedViewPermissionsMap) => presentedViewPermissionsMap[viewId],
    );
  }

  static contextView(contextId: string) {
    return createSelector(
      [
        ViewState.presentedViewMap,
        ViewState.presentedViewForEndUserMap,
        ViewState.contextViewMap,
      ],
      (presentedViewMap, presentedViewForEndUserMap, contextViewMap) => {
        const contextView = contextViewMap[contextId];

        if (!contextView) {
          return null;
        }

        return contextView.endUser
          ? presentedViewForEndUserMap[contextView.value]
          : presentedViewMap[contextView.value];
      },
    );
  }

  static getImplicitView(
    source: string,
    entityId: string,
    requestId?: string,
  ): (state: ViewStateModel) => DataViewsPresenterModel {
    return createSelector([ViewState], (state: ViewStateModel) => {
      const viewId =
        state.implicitView[_getImplicitViewKey(source, entityId, requestId)];
      return state.presentedView[_getViewKey(entityId, viewId)];
    });
  }

  @Selector([ViewState])
  static selectedView(state: ViewStateModel): DataViewsPresenterModel {
    return state.presentedView[state.selectedView];
  }

  @Selector([ViewState])
  static defaultView(state: ViewStateModel): DataViewsPresenterModel {
    return state.defaultView;
  }

  @Selector([ViewState])
  /**
   * select transitory views.
   * it's valid to be readed once after trigger TransitoryFetchViews action
   */
  static transitoryViews(state: ViewStateModel): DataViewsPresenterModel[] {
    return state.transitoryViews;
  }

  @Selector([ViewState])
  /**
   * select transitory views count.
   */
  static viewsForAdminCount(state: ViewStateModel): number {
    return state.viewsForAdminCount;
  }

  @Selector([ViewState])
  /**
   * select transitory views count.
   */
  static viewsForAdmin(
    state: ViewStateModel,
  ): DataViewsForAdminPresenterModel[] {
    return state.ViewsForAdmin;
  }

  // --------------------------------- ACTIONS ------------------------------

  /**
   * fetch views then you can read them from transitoryViews selector
   */
  @Action(ViewActions.TransitoryFetchViews)
  transitoryFetchViews(
    { patchState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.TransitoryFetchViews,
  ): Observable<any> {
    const where: Where<DataViewDataModel> = {
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
      ...this.getDataViewFilters(payload?.filters),
      ...(payload?.viewId && { id: payload.viewId }),
    };

    return (
      payload?.moduleId
        ? this.dataViewsService.findViewsInModule({
            moduleId: payload.moduleId,
            ...this.apiHelper.prepareFilter<DataViewDataModel>({
              where,
              skip: payload.skip ?? 0,
              limit: payload.limit ?? 20,
            }),
            includeImplicit: payload?.includeImplicitViews,
          })
        : payload?.entityId
          ? this.dataViewsService.find({
              entity: payload.entityId,
              includeImplicit: payload?.includeImplicitViews,

              ...this.apiHelper.prepareFilter<DataViewDataModel>({
                where,
                skip: payload.skip ?? 0,
                limit: payload.limit ?? 20,
              }),
            })
          : this.dataViewsService.findAll({
              ...this.apiHelper.prepareFilter<DataViewDataModel>({
                where,
                skip: payload.skip ?? 0,
                limit: payload.limit ?? 20,
              }),
              includeImplicit: payload?.includeImplicitViews,
            })
    ).pipe(
      tap((res) => {
        patchState({
          transitoryViews: res.map((v) =>
            this.dataViewsFactoryService.createPresenterModel(v),
          ),
        });
      }),
    );
  }
  @Action(ViewActions.FetchViewsForAdmin)
  FetchViewsForAdmin(
    { patchState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchViewsForAdmin,
  ): Observable<any> {
    const where: Where<DataViewDataModel> = {
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
        'dataModule.name.en',
        'dataModule.name.ar',
        'dataEntity.name.en',
        'dataEntity.name.ar',
      ]),
      ...this.getDataViewFilters(payload?.filters),
    };

    return this.dataViewsService
      .getViewsForAdmin({
        ...this.apiHelper.prepareFilter<DataViewDataModel>({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? 20,
        }),
        includeImplicit: payload?.includeImplicitViews,
      })
      .pipe(
        tap((res) => {
          patchState({
            ViewsForAdmin: res,
          });
        }),
      );
  }
  /**
   * fetch views count then you can read them from viewsForAdminCount selector
   */
  @Action(ViewActions.FetchViewsForAdminCount)
  transitoryFetchViewsCount(
    { patchState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchViewsForAdminCount,
  ): Observable<any> {
    let filter = payload.filter;
    if (payload.searchTerm) {
      filter = {
        or: this.apiHelper.prepareSearch(payload.searchTerm, [
          'name.en',
          'name.ar',
          'description.en',
          'description.ar',
          'dataModule.name.en',
          'dataModule.name.ar',
          'dataEntity.name.en',
          'dataEntity.name.ar',
        ]),
        ...payload.filter,
      };
    }
    const where = this.apiHelper.prepareWhere(filter);

    return this.dataViewsService
      .countAllForAdmin({
        where,
        includeImplicit: payload?.includeImplicitViews,
      })
      .pipe(
        tap((res) => {
          patchState({
            viewsForAdminCount: res.count,
          });
        }),
      );
  }

  @Action(ViewActions.FetchViews)
  fetchViews(
    { dispatch, patchState, setState, getState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchViews,
  ): Observable<any> {
    const where = {
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
      ...payload.filters,
    } satisfies Where<DataViewDataModel>;

    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<ViewStateModel>({
        presentedViews: payload?.keepData ? getState().presentedViews : [],
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    return this.dataViewsService
      .find({
        entity: payload.entityId,
        ...this.apiHelper.prepareFilter({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? Number.MAX_SAFE_INTEGER,
        }),
        includeImplicit: payload?.includeImplicitViews,
      })
      .pipe(
        tap((items) => {
          patchState({
            presentedViews: items.map((v) =>
              this.dataViewsFactoryService.createPresenterModel(v),
            ),
          });
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ViewActions.SelectView)
  selectView(
    { getState, patchState, dispatch }: StateContext<ViewStateModel>,
    { entityId, viewId, useExplicitAccessId }: ViewActions.SelectView,
  ): Observable<any> | null {
    const prevSelectedView = getState().selectedView;

    const selectedViewKey = _getViewKey(entityId, viewId);
    const selectedView = getState().presentedView[selectedViewKey];

    patchState({
      selectedView: selectedViewKey,
      // Clear the contextView on each selectedView change
      contextView: {},
    });

    if (!entityId || !selectedView) {
      return null;
    }

    // Resolve breadcrumb tokens:
    this.breadcrumbService.set(BreadcrumbToken.ViewName, selectedView.name);

    // Select the view's data entity as the current entity:
    return dispatch(
      new EntityActions.FetchEntity(
        entityId,
        useExplicitAccessId ? viewId : undefined,
      ),
    ).pipe(
      tap(() =>
        dispatch(
          new EntityActions.SelectEntity({
            entityId,
            clearCachedEntries:
              prevSelectedView && prevSelectedView !== selectedViewKey,
          }),
        ),
      ),
    );
  }

  @Action(ViewActions.SetContextView)
  setContextView(
    { setState }: StateContext<ViewStateModel>,
    { contextId, entityId, viewId }: ViewActions.SetContextView,
  ) {
    const selectedViewKey = _getViewKey(entityId, viewId);

    setState(
      patch<ViewStateModel>({
        contextView: patch({ [contextId]: { value: selectedViewKey } }),
      }),
    );
  }

  @Action(ViewActions.SetContextViewForEndUser)
  setContextViewForEndUser(
    { setState }: StateContext<ViewStateModel>,
    { contextId, viewId }: ViewActions.SetContextViewForEndUser,
  ) {
    setState(
      patch<ViewStateModel>({
        contextView: patch({ [contextId]: { value: viewId, endUser: true } }),
      }),
    );
  }

  @Action(ViewActions.FetchView)
  fetchView(
    { setState, getState }: StateContext<ViewStateModel>,
    { entityId, viewId }: ViewActions.FetchView,
  ): Observable<any> {
    const view = getState().presentedView[_getViewKey(entityId, viewId)];

    if (view) {
      return EMPTY;
    }
    setState(
      patch<ViewStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.dataViewsService
      .findById({ entity: entityId, id: viewId })
      .pipe(
        tap((res) => {
          const presenter =
            this.dataViewsFactoryService.createPresenterModel(res);
          setState(
            patch<ViewStateModel>({
              presentedView: patch({
                [_getViewKey(entityId, viewId)]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                item: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ViewActions.CreateView)
  createView(
    { setState, dispatch }: StateContext<ViewStateModel>,
    { entityId, view }: ViewActions.CreateView,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ViewStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    view.isPublished = false;
    return this.dataViewsService
      .create({
        entity: entityId,
        body: this.dataViewsFactoryService.createDataModel(view),
      })
      .pipe(
        switchMap((res) =>
          this.dataViewsService.findById({
            entity: entityId,
            id: res.id,
          }),
        ),
        tap((res) => {
          const presenter =
            this.dataViewsFactoryService.createPresenterModel(res);
          setState(
            patch<ViewStateModel>({
              lastCreatedView: res.id,
              presentedView: patch({
                [_getViewKey(entityId, res.id)]: presenter,
              }),
              presentedViews: insertItem(presenter),
              page: patch({ totalCount: (c) => c++ }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ViewActions.UpdateView)
  updateView(
    { getState, setState, dispatch }: StateContext<ViewStateModel>,
    { entityId, view }: ViewActions.UpdateView,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ViewStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    const cleanedView = removeNullValues(view);

    return this.dataViewsService
      .updateById({
        entity: entityId,
        id: view.id,
        body: this.dataViewsFactoryService.createDataModel(cleanedView),
      })
      .pipe(
        switchMap(() =>
          this.dataViewsService.findById({
            entity: entityId,
            id: view.id,
          }),
        ),
        tap((res) => {
          const presenter =
            this.dataViewsFactoryService.createPresenterModel(res);
          const indexOfPublishedView = getState().publishedViews.findIndex(
            (v) => v.id === res.id,
          );
          setState(
            patch<ViewStateModel>({
              publishedViews: iif(
                res.isPublished && res.isPinned,
                iif(
                  () => indexOfPublishedView >= 0,
                  updateItem(indexOfPublishedView, presenter),
                  insertItem(presenter),
                ),
                removeItem(indexOfPublishedView),
              ),
              presentedViews: updateItem((v) => v.id === res.id, view),
              presentedView: patch({
                [_getViewKey(entityId, res.id)]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          // if (this._shouldReloadNavigationItems(view, oldView)) {
          dispatch(new NavigationStateActions.FetchItemsPinnedToSidebarV2());
          // } else {
          //   if (
          //     view.isPinned &&
          //     view.isPublished &&
          //     view.authenticated?.enabled
          //   ) {
          //     const module =
          //       view.module &&
          //       this.store.selectSnapshot(
          //         ModuleState.presentedModule(view.module)
          //       );

          //     const navItem: NavigationItem = {
          //       id: view.id,
          //       name: view.name,
          //       icon: view.icon,
          //       isPinned: view.isPinned,
          //       isPublished: view.isPublished,
          //       module: view.module,
          //       type: 'DataViews',
          //     };

          //     dispatch(
          //       new NavigationStateActions.UpsertNavigationItem(navItem, module)
          //     );
          //   } else {
          //     dispatch(
          //       new NavigationStateActions.RemoveNavigationItem(
          //         view.id,
          //         view.module
          //       )
          //     );
          //   }
          // }

          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ViewActions.FetchPublishedViews, { cancelUncompleted: true })
  fetchPublishedViews(
    { patchState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchPublishedViews,
  ): Observable<any> {
    const where: Where<DataViewDataModel> = {
      and: [{ isPublished: true, isPinned: true }],
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
    };

    return this.dataViewsService
      .findAll(
        this.apiHelper.prepareFilter<DataViewDataModel>({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? Number.MAX_SAFE_INTEGER,
          fields: {
            name: true,
            icon: true,
            dataEntity: true,
            id: true,
          },
        }),
      )
      .pipe(
        tap((res) => {
          patchState({
            publishedViews: res.map((v) =>
              this.dataViewsFactoryService.createPresenterModel(v),
            ),
          });
        }),
      );
  }

  @Action(ViewActions.DeleteView)
  deleteView(
    { getState, setState, dispatch }: StateContext<ViewStateModel>,
    { entityId, viewId }: ViewActions.DeleteView,
  ): Observable<void> {
    const { module: moduleId } = getState().presentedViews.find(
      (view) => view.id === viewId,
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ViewStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );

    return this.dataViewsService
      .deleteById({ entity: entityId, id: viewId })
      .pipe(
        tap(() => {
          setState(
            patch<ViewStateModel>({
              presentedViews: removeItem((v) => v.id === viewId),
              presentedView: patch({
                [_getViewKey(entityId, viewId)]: undefined,
              }),
              publishedViews: removeItem((v) => v.id === viewId),
            }),
          );

          // Remove from sidebar if it's already there:
          dispatch(
            new NavigationStateActions.RemoveNavigationItem(viewId, moduleId),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ViewActions.FetchDefaultView)
  fetchDefaultDataView(
    { patchState }: StateContext<ViewStateModel>,
    { entityId }: ViewActions.FetchDefaultView,
  ): Observable<any> {
    return this.dataViewsService.findDefaultView({ entity: entityId }).pipe(
      tap((res) => {
        if (res) {
          patchState({
            defaultView: this.dataViewsFactoryService.createPresenterModel(res),
          });
        }
      }),
    );
  }

  @Action(ViewActions.GetCountsByTypes)
  getCountsByTypes(
    { setState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.GetCountsByTypes,
  ): Observable<any> {
    const prepareWhere = (permissions: any) =>
      this.apiHelper.prepareWhere({
        ...payload.filter,
        and: [...permissions, ...(payload?.filter?.and ?? '')],
      });

    return forkJoin([
      this.dataViewsService.count({
        entity: payload.entityId,
        where: prepareWhere(anonymousViews),
        includeImplicit: true,
      }), // anonymous
      this.dataViewsService.count({
        entity: payload.entityId,
        where: prepareWhere(authenticatedViews),
        includeImplicit: true,
      }), // authenticated
      this.dataViewsService.count({
        entity: payload.entityId,
        where: prepareWhere(RestrictedViews),
        includeImplicit: true,
      }), // restricted
    ]).pipe(
      tap(([anonymous, authenticated, restricted]) => {
        setState(
          patch<ViewStateModel>({
            counts: {
              anonymous: anonymous.count,
              authenticated: authenticated.count,
              restricted: restricted.count,
            },
          }),
        );
      }),
    );
  }

  @Action(ViewActions.FetchViewForEndUser)
  fetchViewForEndUser(
    { setState, getState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchViewForEndUser,
  ): Observable<any> {
    const { viewId, useExplicitAccessId } = payload;
    const cached = getState().presentedViewForEndUser?.[viewId];
    if (cached) {
      return of(cached);
    }
    return this.dataViewsService
      .findViewForEndUserWithRedirect({
        id: viewId,
        context: useExplicitAccessId
          ? new ManagedHttpContext({ publicAccessId: viewId })
          : undefined,
      })
      .pipe(
        tap((view) => {
          const presenter =
            this.endUserDataViewsFactoryService.createPresenterModel(view);

          setState(
            patch<ViewStateModel>({
              presentedViewForEndUser: patch({
                [viewId]: presenter,
              }),
              // Since the bread crumb use the presentedView to read the view name, we need to add the fetched view in both properties
              presentedView: patch({
                [_getViewKey(presenter.dataEntity, viewId)]: presenter,
              }),
            }),
          );
        }),
        catchError(() => {
          setState(
            patch<ViewStateModel>({
              presentedViewForEndUser: patch({
                [viewId]: null,
              }),
            }),
          );
          return EMPTY;
        }),
      );
  }

  @Action(ViewActions.FetchViewPermissionsForEndUser)
  fetchViewPermissionsForEndUser(
    { setState }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchViewPermissionsForEndUser,
  ): Observable<any> {
    const { viewId } = payload;

    return this.dataViewsService
      .findViewPermissionsForEndUser({
        id: viewId,
      })
      .pipe(
        tap((viewPermissionsModel) => {
          const presenter =
            this.dataViewsPermissionsForEndUserFactoryService.createPresenterModel(
              viewPermissionsModel,
            );

          setState(
            patch<ViewStateModel>({
              presentedViewPermissionsForEndUser: patch({
                [viewId]: presenter,
              }),
            }),
          );
        }),
        catchError(() => {
          setState(
            patch<ViewStateModel>({
              presentedViewPermissionsForEndUser: patch({
                [viewId]: null,
              }),
            }),
          );
          return EMPTY;
        }),
      );
  }

  // TODO discuss with Amer about where to place the common view/generic view actions
  @Action(ViewActions.FetchAnonymousView)
  fetchAnonymousView(
    { setState, dispatch }: StateContext<ViewStateModel>,
    { payload }: ViewActions.FetchAnonymousView,
  ): Observable<any> {
    dispatch(new LoadingActions.SetLoading(true));
    const token = payload.token;
    return this.dataViewsService.findAnonymousViewByToken(token).pipe(
      tap((view) => {
        setState(
          patch<ViewStateModel>({
            lastFetchedView: view,
          }),
        );
        dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(ViewActions.FetchServiceImplicitViewForRequest)
  canAccessRequest(
    { getState, setState, dispatch }: StateContext<ViewStateModel>,
    {
      entityId,
      source,
      requestId,
      isDraft,
    }: ViewActions.FetchServiceImplicitViewForRequest,
  ): Observable<any> {
    const { implicitView } = getState();

    // No need to check the implicit view if we already have it:
    if (implicitView[_getImplicitViewKey(source, entityId, requestId)]) {
      return EMPTY;
    }

    // Based on API's requirements:
    const requestMode =
      isDraft || !requestId ? 'New' : serviceRequestSourceMap.get(source);

    return this.requestControllerService
      .canAccessRequest({
        id: isDraft || !requestId ? 'r' : requestId,
        entity: entityId,
        mode: requestMode,
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((view) => {
          if (!view) {
            return;
          }
          const presenter =
            this.dataViewsFactoryService.createPresenterModel(view);

          setState(
            patch<ViewStateModel>({
              implicitView: patch({
                [_getImplicitViewKey(source, entityId, requestId)]:
                  presenter.id,
              }),
              presentedView: patch({
                [_getViewKey(entityId, presenter.id)]: presenter,
              }),
            }),
          );
          dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }

  @Action(ViewActions.FetchViewByRelation)
  fetchViewByRelation(
    { setState, getState }: StateContext<ViewStateModel>,
    {
      entityId,
      viewId,
      relationId,
      relatedEntryId,
      mainViewId,
    }: ViewActions.FetchViewByRelation,
  ): Observable<any> {
    const relationViewKey = _getRelationViewKey(
      entityId,
      viewId,
      relationId,
      relatedEntryId,
    );

    setState(
      patch<ViewStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.dataViewsService
      .findRelationViewById({
        entity: entityId,
        id: viewId,
        relationId,
        relatedEntryId,
        mainViewId,
      })
      .pipe(
        tap((res) => {
          const presenter =
            this.dataViewsFactoryService.createPresenterModel(res);
          setState(
            patch<ViewStateModel>({
              presentedViewByRelation: patch({
                [relationViewKey]: presenter,
              }),
              // Also store in presentedView so contextView selector can find it
              presentedView: patch({
                [_getViewKey(presenter.dataEntity, presenter.id)]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<ViewStateModel>({
              loading: patch<LoadingIndicator>({
                item: false,
              }),
            }),
          );
        }),
      );
  }

  private getDataViewFilters(
    dataViewFlags: DataViewFilters,
  ): Condition<DataViewDataModel> {
    let dataViewSourceFilter;
    let dataQueryFilter;
    if (
      dataViewFlags?.dataSourceType !== undefined &&
      dataViewFlags?.withAllEntriesSourceType
    ) {
      dataViewSourceFilter = {
        or: [
          {
            'dataSource.type': dataViewFlags?.dataSourceType,
          },
          {
            'dataSource.type': 0,
          },
        ],
      } as Where<DataViewDataModel>;
    } else if (dataViewFlags?.dataSourceType !== undefined) {
      dataViewSourceFilter = {
        'dataSource.type': dataViewFlags?.dataSourceType,
      } as Where<DataViewDataModel>;
    }

    if (
      dataViewFlags?.dataSourceQuery !== undefined &&
      dataViewFlags.withAllEntriesSourceType
    ) {
      dataQueryFilter = {
        or: [
          {
            'dataSource.query': '',
          },
          {
            ['dataSource.query']: dataViewFlags?.dataSourceQuery,
          },
        ],
      } as Where<DataViewDataModel>;
    } else if (dataViewFlags?.dataSourceQuery !== undefined) {
      dataQueryFilter = {
        ['dataSource.query']: dataViewFlags?.dataSourceQuery,
      } as Where<DataViewDataModel>;
    }

    return {
      ...(dataViewFlags?.viewConfigurationEnabled !== undefined && {
        ['viewConfiguration.enabled']: dataViewFlags.viewConfigurationEnabled,
      }),
      ...(dataViewFlags?.published !== undefined && {
        ['isPublished']: dataViewFlags.published,
      }),
      ...(dataViewFlags?.pinnedToSideBar !== undefined && {
        ['isPinned']: dataViewFlags.pinnedToSideBar,
      }),
      ...(dataViewFlags?.filterFirstDisabled === true && {
        ['viewConfiguration.filterFirst.enabled']: {
          neq: true,
        },
      }),
      ...(dataViewFlags?.filterViewType != null && {
        ['viewConfiguration.availableDisplays.viewType']:
          dataViewFlags?.filterViewType,
      }),
      ...(!!dataViewSourceFilter && dataViewSourceFilter),
      ...(!!dataQueryFilter && dataQueryFilter),
    } satisfies Condition<DataViewDataModel>;
  }

  @Action(AppStateActions.AppContextChanged)
  clearViews({ patchState }: StateContext<ViewStateModel>) {
    patchState({
      selectedView: null,
      presentedView: {},
      presentedViewForEndUser: {},
      implicitView: {},
    });
  }
}

function _getViewKey(entityId: string, viewId: string): string {
  return (entityId ? `${entityId}:` : '') + (viewId ?? '');
}

function _getRelationViewKey(
  entityId: string,
  viewId: string,
  relationId: string,
  relatedEntryId?: string,
): string {
  return `${entityId}:${viewId}:${relationId}${relatedEntryId ? `:${relatedEntryId}` : ''}`;
}

function _getImplicitViewKey(
  source: string,
  entityId: string,
  requestId?: string,
): string {
  return `${source}:${entityId}` + (requestId ? `:${requestId}` : '');
}
